import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { AppNotification } from '../../api/notificationsApi';
import {
  formatNotificationTime,
  notificationTypeEmoji,
} from '../../utils/notificationDisplay';

interface NotificationListItemProps {
  notification: AppNotification;
  onClick: () => void;
  onDelete?: () => void;
  compact?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  selectionMode?: boolean;
}

export const NotificationListItem: React.FC<NotificationListItemProps> = ({
  notification,
  onClick,
  onDelete,
  compact = false,
  selected = false,
  onToggleSelect,
  selectionMode = false,
}) => {
  const unread = !notification.isRead;

  return (
    <div
      className={`flex items-start gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0 transition-colors ${
        unread ? 'bg-blue-50/60' : 'bg-white hover:bg-gray-50'
      } ${compact ? '' : ''}`}
    >
      {selectionMode && onToggleSelect && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1.5 rounded border-gray-300 text-blue-600"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <button
        type="button"
        onClick={onClick}
        className="flex flex-1 min-w-0 text-left gap-3"
      >
        <span
          className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-lg"
          aria-hidden
        >
          {notificationTypeEmoji(notification.type)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <p
              className={`text-sm truncate flex-1 ${unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}
            >
              {notification.title}
            </p>
            {unread && (
              <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-600" aria-label="Chưa đọc" />
            )}
          </div>
          {!compact && notification.message && (
            <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{notification.message}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-gray-500">
            <span>{formatNotificationTime(notification.createdAt)}</span>
            {notification.building?.name && (
              <>
                <span>·</span>
                <span className="truncate max-w-[10rem]">{notification.building.name}</span>
              </>
            )}
          </div>
        </div>
      </button>
      {onDelete && !selectionMode && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
          title="Xóa"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
